import { styled } from "goober";
import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { BuildStatus } from "../BuildStatus";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { ListTable } from "../ListTable";
import { SearchField } from "../Settings/shared";
import { TabbedModal } from "../TabbedModal";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import { computeAiBranchRows } from "./autofixAutotune";
import {
  DETAIL_MODAL_STYLE,
  DetailModalContent,
  ModalTitleIcon,
  SummaryCard,
} from "./MonitoringDetailCard";
import { useMonitoringInteraction } from "./analytics";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { useMonitoringScope } from "./useMonitoringScope";
import { PanelSkeleton } from "./workflowsLayout";

const SplitRow = styled("div")`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 24px;
  gap: 16px;
`;

const SplitStat = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
`;

type View = "autofix" | "autotune";

export function AutofixAutotunePanel() {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const formatRelativeTime = useFormatRelativeTime();
  const [data, result] = useQuery(queries.monitorAutofixAutotune());
  const { matchesSpace } = useMonitoringScope(routes.monitoring.workflows);
  const displayName = useSpaceDisplayName();
  const { panel, search: searchParam } = useCurrentUrl(
    routes.monitoring.workflows
  );
  const open = panel === "autofix" || panel === "autotune";
  const view: View = panel === "autotune" ? "autotune" : "autofix";
  const search = searchParam ?? "";
  const setView = (next: View) => {
    recordInteraction("workflows.autofix_autotune_view_toggle", {
      value: next,
    });
    navigate(
      routes.monitoring.workflows,
      { panel: next, search: "" },
      { replace: true }
    );
  };
  const setSearch = (value: string) =>
    navigate(routes.monitoring.workflows, { search: value }, { replace: true });
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", search: "" },
      { replace: true }
    );

  const { autofix, autotune } = useMemo(() => {
    const rows = computeAiBranchRows(data ?? []);
    return {
      autofix: rows.autofix.filter((row) => matchesSpace(row.spaceId)),
      autotune: rows.autotune.filter((row) => matchesSpace(row.spaceId)),
    };
  }, [data, matchesSpace]);

  const workflowById = useMemo(
    () => new Map((data ?? []).map((w) => [w.id, w] as const)),
    [data]
  );

  const total = autofix.length + autotune.length;

  if (result.type !== "complete") {
    return <PanelSkeleton />;
  }

  const activeRows = view === "autofix" ? autofix : autotune;
  const q = search.trim().toLowerCase();
  const filtered = q
    ? activeRows.filter((r) => r.workflowName.toLowerCase().includes(q))
    : activeRows;

  return (
    <>
      <SummaryCard
        title="Autofix and Autotune"
        stacked
        onOpen={
          total > 0
            ? () => {
                const next = autofix.length > 0 ? "autofix" : "autotune";
                recordInteraction("workflows.panel_drilldown_open", {
                  value: next,
                });
                navigate(routes.monitoring.workflows, {
                  panel: next,
                  search: "",
                });
              }
            : undefined
        }
        accessory={
          <SplitRow>
            <SplitStat>
              <Icon name="radar" size={16} />
              <Text variant="text-regular-md" color="var(--text-primary)">
                {autofix.length}
              </Text>
            </SplitStat>
            <SplitStat>
              <Icon name="audio-waveform" size={16} />
              <Text variant="text-regular-md" color="var(--text-primary)">
                {autotune.length}
              </Text>
            </SplitStat>
          </SplitRow>
        }
      />

      {open && (
        <TabbedModal
          icon={
            <ModalTitleIcon
              icon={view === "autofix" ? "radar" : "audio-waveform"}
            />
          }
          title="Autofix and Autotune"
          tabs={[
            {
              key: "autofix",
              label: "Autofix",
              icon: "radar",
              count: autofix.length,
            },
            {
              key: "autotune",
              label: "Autotune",
              icon: "audio-waveform",
              count: autotune.length,
            },
          ]}
          activeTab={view}
          onTabChange={setView}
          onClose={close}
          style={DETAIL_MODAL_STYLE}
          footerLeft={
            <Text variant="text-small" color="var(--text-secondary)">
              {total} {total === 1 ? "branch" : "branches"} awaiting review
            </Text>
          }
          footerRight={
            <Button size="l" variant="primary" onClick={close}>
              Done
            </Button>
          }
        >
          <DetailModalContent>
            <ListSection
              title={view === "autofix" ? "Autofix" : "Autotune"}
              description={
                view === "autofix"
                  ? "Fixes 3B proposed for failing workflows, awaiting review."
                  : "Improvements 3B proposed for your workflows, awaiting review."
              }
              action={
                <SearchField
                  placeholder="Search workflows…"
                  value={search}
                  onChange={setSearch}
                  width={280}
                  autoFocus
                />
              }
            >
              <ListTable
                headers={{
                  name: "Workflow",
                  status: "Status",
                  updated: "Updated",
                }}
                columns={{ name: "flex", status: "md", updated: "md" }}
                emptyState={
                  <Text variant="text-regular" color="var(--text-secondary)">
                    {search
                      ? "No workflows match your search."
                      : `No ${view} branches awaiting review.`}
                  </Text>
                }
                items={filtered.map((row) => {
                  const space = workflowById.get(row.workflowId)?.space;
                  return {
                    key: row.branchId,
                    link: makeUrl(routes.space.workflow, {
                      spaceId: row.spaceId,
                      workflowId: row.workflowId,
                      branchId: row.branchId,
                    }),
                    onNavigate: () =>
                      recordInteraction("workflows.workflow_link_click", {
                        targetType: "workflow",
                        targetId: row.workflowId,
                      }),
                    name: (hover: boolean) => (
                      <NameCell>
                        <WorkflowBadge
                          workflow={{
                            id: row.workflowId,
                            spaceId: row.spaceId,
                            badgeSeed: row.badgeSeed,
                            pausedAt: row.pausedAt,
                            branches: [],
                          }}
                          hover={hover}
                          blueprint
                        />
                        <NameBody>
                          <Text variant="text-regular-md" ellipsis>
                            {row.workflowName}
                          </Text>
                          {space ? (
                            <Text
                              variant="text-small"
                              color="var(--text-secondary)"
                              ellipsis
                            >
                              {displayName(space)}
                            </Text>
                          ) : null}
                        </NameBody>
                      </NameCell>
                    ),
                    status: <BuildStatus status={row.status} />,
                    updated: (
                      <Text variant="text-small" color="var(--text-secondary)">
                        {formatRelativeTime(row.lastActivity)}
                      </Text>
                    ),
                  };
                })}
              />
            </ListSection>
          </DetailModalContent>
        </TabbedModal>
      )}
    </>
  );
}
